import { defineStore } from 'pinia'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  updateEmail,
} from 'firebase/auth'
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
} from 'firebase/firestore'
import { auth, db } from '@/firebase'

export const useUserStore = defineStore('user', {
  state: () => ({
    profile: {},
    votes: [],
    loadingProfile: false,
    loadingVotes: false,
    // True once getVotesForUser has returned at least one response for the
    // current meeting. Dashboard uses this to distinguish "no votes loaded
    // yet" from "zero votes exist".
    votesLoaded: false,
  }),

  getters: {
    isAuthenticated: (state) => !!state.profile && !!state.profile.uid,
    isAdmin: (state) => state.profile && state.profile.role === 'admin',
    isShareholder: (state) => !!state.profile?.shn,
  },

  actions: {
    async login(user) {
      await this.fetchUserProfile(user)
    },

    async signInEmail(email, password) {
      const cred = await signInWithEmailAndPassword(auth, email, password)
      await this.fetchUserProfile(cred.user)
      return cred.user
    },

    async resetPassword(email) {
      await sendPasswordResetEmail(auth, email)
    },

    async signup(form) {
      const cred = await createUserWithEmailAndPassword(auth, form.email, form.password)
      await setDoc(doc(db, 'users', cred.user.uid), {
        name: form.name,
        shn: form.shn,
        shares: form.shares,
        created: new Date(),
      })
      await this.fetchUserProfile(cred.user)
      return cred.user
    },

    async fetchUserProfile(user) {
      if (!user) {
        this.profile = {}
        this.votes = []
        return
      }
      this.loadingProfile = true
      try {
        const snap = await getDoc(doc(db, 'users', user.uid))
        const data = snap.exists() ? snap.data() : {}
        this.profile = {
          uid: user.uid,
          email: user.email,
          ...data,
        }
        // Votes are meeting-scoped; callers should invoke
        // getVotesForUser(meetingId) once the active meeting is known.
      } finally {
        this.loadingProfile = false
      }
    },

    // Loads the current user's vote records for a specific meeting.
    // Votes are always meeting-scoped to avoid mixing in history from
    // prior years on the dashboard.
    async getVotesForUser(meetingId) {
      if (!auth.currentUser || !meetingId) {
        this.votes = []
        this.votesLoaded = false
        return
      }
      this.loadingVotes = true
      try {
        const q = query(
          collection(db, 'votes'),
          where('user', '==', auth.currentUser.uid),
          where('meeting', '==', meetingId),
          orderBy('createdOn', 'desc'),
        )
        const snap = await getDocs(q)
        this.votes = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        this.votesLoaded = true
      } catch (err) {
        console.error('getVotesForUser error', err)
        this.votes = []
      } finally {
        this.loadingVotes = false
      }
    },

    async signout() {
      await signOut(auth)
      this.profile = {}
      this.votes = []
      this.votesLoaded = false
    },

    async updateProfile(patch) {
      if (!auth.currentUser) return
      const uid = auth.currentUser.uid
      const userRef = doc(db, 'users', uid)
      const updates = {}
      if (patch.name) updates.name = patch.name
      if (Object.keys(updates).length) await updateDoc(userRef, updates)
      if (patch.email && patch.email !== auth.currentUser.email) {
        await updateEmail(auth.currentUser, patch.email)
      }
      await this.fetchUserProfile(auth.currentUser)
    },
  },
})
